import React, { useEffect, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { RefreshCw, Save, Coins } from 'lucide-react';
import { toast } from 'sonner';

export default function ForexView() {
  const [rates, setRates] = useState([]);
  const [edits, setEdits] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    ConstructionAPI.getForex().then(d => {
      setRates(d.rates || []);
      setUpdatedAt(d.updated_at);
      setEdits({});
    });
  };

  useEffect(load, []);

  const save = async () => {
    const updates = Object.entries(edits).map(([currency, rate_per_usd]) => ({ currency, rate_per_usd: Number(rate_per_usd) }));
    if (!updates.length) return toast.info('No changes to save');
    try {
      setSaving(true);
      await ConstructionAPI.updateForex(updates);
      toast.success(`Updated ${updates.length} rate(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="forex-view">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-600 rounded-lg"><Coins className="h-5 w-5 text-white" /></div>
            <div>
              <CardTitle className="text-base">Forex Rates (Indicative)</CardTitle>
              <p className="text-xs text-slate-500">USD base. Values are indicative — adjust to your own treasury rate.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Reload</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="forex-save"><Save className="h-3.5 w-3.5 mr-1.5" />{saving ? 'Saving…' : 'Save changes'}</Button>
          </div>
        </div>
        {updatedAt && <p className="text-xs text-slate-500 mt-1">Last updated: {new Date(updatedAt).toLocaleString()}</p>}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {rates.map(r => (
            <div key={r.currency} className="border border-slate-200 rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{r.currency}</span>
                <span className="text-sm text-slate-500">{r.symbol}</span>
              </div>
              <div className="text-xs text-slate-500 mb-2 truncate">{r.name}</div>
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">1 USD =</div>
              <Input
                type="number"
                step="0.0001"
                value={edits[r.currency] ?? r.rate_per_usd}
                onChange={e => setEdits(prev => ({ ...prev, [r.currency]: e.target.value }))}
                className="h-8"
                data-testid={`forex-input-${r.currency}`}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
